import type { ConditionAlertRowViewModel, TableStatus } from '.';
import type { AlertRowAction } from './state/actions';
import { alertRowReducer, hasEmptyConditionValue } from './state/reducer';
import { createInitialAlertRowState } from './state/types';
import type { AlertCondition, AlertRowState, ConditionOperation } from './state/types';
import { signal, effect, computed, untracked, batch } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import type { OutputColumn } from '@thinkalpha/platform-ws-client/contracts/table.js';
import { getPriceDirectionFromPrices, getDirectionFromSign } from '@thinkalpha/ui-core/priceDirection';
import type { PriceDirection } from '@thinkalpha/ui-core/priceDirection';
import { injectable, inject } from 'src/features/ioc';
import { instrumentToString } from 'src/lib/instrumentUtils';
import { createTable as createTableApi } from 'src/lib/table';
import { OutputStage, SourceStage } from 'src/lib/tableStages';
import { buildNotifications } from 'src/models/AlertsWidgetModel/notifications';
import type { ConditionAlertModel } from 'src/services/AlertService/AlertModel';
import type { NewConditionAlertDefinition } from 'src/services/AlertService/types';
import type { SlugMapService } from 'src/services/SlugMapService';
import type { ReactBindings } from 'src/types/bindings';

const INDICATOR_VALUE_COLUMN_ID = '__indicator_value__';

/**
 * Implementation of ConditionAlertRowViewModel that wraps a ConditionAlertModel from the service.
 * Uses a reducer pattern for state management - all state changes go through dispatch().
 * Handles its own table subscription for displaying the alert's configured indicator value.
 * Can also represent a "pending" alert that hasn't been confirmed by the server yet.
 */
/**
 * Callback type for when the view model's state changes.
 * Used by AlertsWidgetModel to sync changes back to AlertService.
 */
export type StateChangeCallback = (alertId: string, state: AlertRowState) => void;

@injectable()
export class ConditionAlertRowViewModelImpl implements ConditionAlertRowViewModel, Disposable {
    #alertModel: ConditionAlertModel | null = null;
    #id: string = '';
    #instrument: Instrument = null!;

    /** Core state managed by the reducer */
    readonly #state = signal<AlertRowState>(createInitialAlertRowState());

    /** Subscription-derived state (not part of reducer, updated by table subscription) */
    readonly #connectionStatus = signal<TableStatus>('connecting');
    readonly #indicatorValue = signal<number | undefined>(undefined);
    readonly #priceDirection = signal<PriceDirection>('unchanged');
    readonly #isTriggered = signal<boolean>(false);

    readonly #disposableStack = new DisposableStack();
    #initialized = false;
    /** Tracks the currently subscribed indicator (id:version) so we can detect changes */
    #subscribedIndicatorKey: string | undefined;
    /** Callback called when state changes, set by AlertsWidgetModel */
    #onStateChange: StateChangeCallback | null = null;

    constructor(
        @inject('SlugMapService') private readonly slugMapService: SlugMapService,
        @inject('TableModelProvider') private readonly tableModelProvider: ReactBindings['TableModelProvider'],
        @inject('Logger') private logger: ReactBindings['Logger'],
    ) {
        this.logger = logger.getSubLogger({ name: 'ConditionAlertRowViewModel' });
    }

    get alertModel(): ConditionAlertModel | null {
        return this.#alertModel;
    }

    get id(): string {
        return this.#id;
    }

    get instrument(): Instrument {
        return this.#instrument;
    }

    get instrumentString(): string {
        return instrumentToString(this.#instrument);
    }

    // === State signal getters (derived from #state for convenience) ===

    get state(): ReadonlySignal<AlertRowState> {
        return this.#state;
    }

    readonly #conditionSignal = computed(() => this.#state.value.condition);
    get condition(): ReadonlySignal<AlertCondition> {
        return this.#conditionSignal;
    }

    readonly #enabledSignal = computed(() => this.#state.value.enabled);
    get enabled(): ReadonlySignal<boolean> {
        return this.#enabledSignal;
    }

    readonly #showToastSignal = computed(() => this.#state.value.showToast);
    get showToast(): ReadonlySignal<boolean> {
        return this.#showToastSignal;
    }

    readonly #playSoundSignal = computed(() => this.#state.value.playSound);
    get playSound(): ReadonlySignal<boolean> {
        return this.#playSoundSignal;
    }

    readonly #soundTypeSignal = computed(() => this.#state.value.soundType);
    get soundType(): ReadonlySignal<string> {
        return this.#soundTypeSignal;
    }

    readonly #speakTtsSignal = computed(() => this.#state.value.speakTts);
    get speakTts(): ReadonlySignal<boolean> {
        return this.#speakTtsSignal;
    }

    readonly #ttsTextSignal = computed(() => this.#state.value.ttsText);
    get ttsText(): ReadonlySignal<string | undefined> {
        return this.#ttsTextSignal;
    }

    readonly #ttsVoiceUriSignal = computed(() => this.#state.value.ttsVoiceUri);
    get ttsVoiceUri(): ReadonlySignal<string | undefined> {
        return this.#ttsVoiceUriSignal;
    }

    readonly #ttsRateSignal = computed(() => this.#state.value.ttsRate);
    get ttsRate(): ReadonlySignal<number> {
        return this.#ttsRateSignal;
    }

    readonly #ttsPitchSignal = computed(() => this.#state.value.ttsPitch);
    get ttsPitch(): ReadonlySignal<number> {
        return this.#ttsPitchSignal;
    }

    readonly #ttsVolumeSignal = computed(() => this.#state.value.ttsVolume);
    get ttsVolume(): ReadonlySignal<number> {
        return this.#ttsVolumeSignal;
    }

    readonly #noteSignal = computed(() => this.#state.value.note);
    get note(): ReadonlySignal<string | null> {
        return this.#noteSignal;
    }

    readonly #localOperationSignal = computed(() => this.#state.value.localOperation);
    get localOperation(): ReadonlySignal<ConditionOperation> {
        return this.#localOperationSignal;
    }

    // === Subscription-derived state (not part of reducer) ===

    get connectionStatus(): ReadonlySignal<TableStatus> {
        return this.#connectionStatus;
    }

    get isTriggered(): ReadonlySignal<boolean> {
        return this.#alertModel?.isTriggered ?? this.#isTriggered;
    }

    get indicatorValue(): ReadonlySignal<number | undefined> {
        return this.#indicatorValue;
    }

    get priceDirection(): ReadonlySignal<PriceDirection> {
        return this.#priceDirection;
    }

    readonly #hasEmptyValue = computed(() => hasEmptyConditionValue(this.#state.value.condition));

    get hasEmptyValue(): ReadonlySignal<boolean> {
        return this.#hasEmptyValue;
    }

    // === Dispatch method ===

    /**
     * Set the callback for state changes.
     * Called by AlertsWidgetModel to receive updates for syncing to AlertService.
     */
    setOnStateChange(callback: StateChangeCallback | null): void {
        this.#onStateChange = callback;
    }

    /**
     * Dispatch an action to update state.
     * Components should call this method instead of directly modifying signals.
     */
    dispatch(action: AlertRowAction): void {
        const currentState = this.#state.value;
        const newState = alertRowReducer(currentState, action);

        if (newState === currentState) {
            return; // No change
        }

        this.#state.value = newState;

        if (TRACE) {
            this.logger.trace('Action dispatched', {
                alertId: this.#id,
                actionType: action.type,
            });
        }

        // Notify parent of state change (unless syncing from external source)
        // SYNC_FROM_DEFINITION is triggered by ConditionAlertModel updates, so don't re-sync those
        if (action.type !== 'SYNC_FROM_DEFINITION' && this.#onStateChange) {
            this.#onStateChange(this.#id, newState);
        }
    }

    // === Initialization ===

    /**
     * Initialize the view model from a ConditionAlertModel.
     * Sets up reactive sync from the model's definition and subscribes to indicator value.
     */
    initFromConditionAlertModel(model: ConditionAlertModel): void {
        if (this.#initialized) {
            this.logger.warn('ConditionAlertRowViewModel already initialized', { alertId: model.id });
            return;
        }
        this.#initialized = true;
        this.#alertModel = model;
        this.#id = model.id;
        this.#instrument = model.instrument;

        // Get the initial definition and sync to state (untracked to avoid triggering effects)
        const initialDef = untracked(() => model.definition.value);
        // Batch the state update to avoid signal cycles during initialization
        batch(() => {
            this.dispatch({ type: 'SYNC_FROM_DEFINITION', definition: initialDef });
        });

        // Subscribe to indicator value for display (now condition is set)
        this.#setupIndicatorSubscription(model.instrument);

        // Track the last definition we synced to avoid re-syncing our own updates
        let lastSyncedDef = initialDef;

        // Sync state from ConditionAlertModel.definition for future changes (when server updates)
        this.#disposableStack.defer(
            effect(() => {
                const def = model.definition.value;
                // Only sync if the definition actually changed (from the server)
                // This avoids cycles where we dispatch, which syncs to server, which triggers this effect
                if (def === lastSyncedDef) {
                    return;
                }
                lastSyncedDef = def;
                // Batch the update to avoid signal cycles
                batch(() => {
                    this.dispatch({ type: 'SYNC_FROM_DEFINITION', definition: def });
                });
            }),
        );

        // Connection status is 'connected' once we have the model
        this.#connectionStatus.value = 'connected';

        if (TRACE) {
            const condition = initialDef.condition;
            this.logger.trace('ConditionAlertRowViewModel initialized', {
                alertId: model.id,
                instrument: instrumentToString(model.instrument),
                conditionType: condition?.type,
                indicatorId: condition?.type === 'quantitative' ? condition.indicator?.id : undefined,
            });
        }
    }

    /**
     * Set up an effect that waits for the slug map to be available, then subscribes to the indicator value.
     * This handles the timing issue where alerts may be initialized before the slug map is loaded.
     * Re-subscribes when the indicator changes (e.g., user changes from Last Price to Change Close).
     */
    #setupIndicatorSubscription(instrument: Instrument): void {
        // Track previous value for direction comparison (must be outside effect to persist across runs)
        let previousValue: number | undefined;
        // Track disposables for the current table subscription so we can clean up on indicator change
        let tableSubscriptionDisposables: DisposableStack | undefined;
        // Track current subscription setup to cancel if a new one starts before it completes
        let currentSubscriptionId = 0;

        this.#disposableStack.defer(
            effect(() => {
                const condition = this.#state.value.condition;
                if (condition?.type !== 'quantitative' || !condition.indicator) {
                    if (TRACE) {
                        this.logger.trace('No indicator to subscribe to', { alertId: this.#id });
                    }
                    // Clean up any existing subscription when condition no longer has an indicator
                    if (tableSubscriptionDisposables) {
                        tableSubscriptionDisposables.dispose();
                        tableSubscriptionDisposables = undefined;
                    }
                    this.#subscribedIndicatorKey = undefined;
                    this.#indicatorValue.value = undefined;
                    this.#priceDirection.value = 'unchanged';
                    previousValue = undefined;
                    return;
                }

                // Wait for slug map to be available
                const slugMap = this.slugMapService.slugMap.value;
                if (!slugMap) {
                    if (TRACE) {
                        this.logger.trace('Waiting for slugMap', { alertId: this.#id });
                    }
                    return;
                }

                const tickerIndicator = slugMap[Slugs.symbol];
                if (!tickerIndicator) {
                    this.logger.warn('Symbol indicator not found in slugMap', { alertId: this.#id });
                    return;
                }

                const indicatorRef = condition.indicator;
                const indicatorKey = indicatorRef.id;

                // Check if we're already subscribed to this indicator (same id)
                if (this.#subscribedIndicatorKey === indicatorKey) {
                    return;
                }

                // If we had a previous subscription, clean it up
                if (tableSubscriptionDisposables) {
                    if (TRACE) {
                        this.logger.trace('Indicator changed, cleaning up previous subscription', {
                            alertId: this.#id,
                            previousIndicatorKey: this.#subscribedIndicatorKey,
                            newIndicatorKey: indicatorKey,
                        });
                    }
                    tableSubscriptionDisposables.dispose();
                    tableSubscriptionDisposables = undefined;
                    // Reset value and direction when switching indicators
                    this.#indicatorValue.value = undefined;
                    this.#priceDirection.value = 'unchanged';
                    previousValue = undefined;
                }

                this.#subscribedIndicatorKey = indicatorKey;
                const subscriptionId = ++currentSubscriptionId;

                const instrumentStr = instrumentToString(instrument);

                // Determine if this is a "change" indicator (direction based on sign rather than tick-to-tick)
                // Change indicators show green for positive values, red for negative
                // Check if indicator name contains "Change" (covers "Change Close", "Change Close %", etc.)
                const indicatorEntry = Object.values(slugMap).find((entry) => entry.id === indicatorRef.id);
                const isChangeIndicator = indicatorEntry?.name?.toLowerCase().includes('change') ?? false;

                // Log if indicator not found in slug map (may affect display/formatting)
                if (!indicatorEntry) {
                    this.logger.debug('Indicator not found in slug map (name lookup failed)', {
                        alertId: this.#id,
                        indicatorId: indicatorRef.id,
                        instrument: instrumentStr,
                        slugMapSize: Object.keys(slugMap).length,
                    });
                }

                if (TRACE) {
                    this.logger.trace('Setting up indicator subscription', {
                        alertId: this.#id,
                        indicatorId: indicatorRef.id,
                        indicatorName: indicatorEntry?.name,
                        instrument: instrumentStr,
                        isChangeIndicator,
                    });
                }

                // Build table query (same pattern as alerts worker)
                const sourceStage = SourceStage.allForKey('ticker');

                const filterStage = sourceStage.intoFilter({
                    type: 'filter',
                    formula: `_symbol == "${instrumentStr}"`,
                    imports: [{ alias: '_symbol', id: tickerIndicator.id }],
                });

                // Add indicator value column
                const indicatorColumn: OutputColumn = {
                    id: INDICATOR_VALUE_COLUMN_ID,
                    type: 'indicator',
                    indicator: { id: indicatorRef.id },
                };

                const outputStage = OutputStage.fromInputStage(filterStage, {}, [indicatorColumn]);
                const tablePayload = outputStage.intoTable(undefined);

                // Create a new TableModel instance asynchronously
                this.tableModelProvider().then((tableModel) => {
                    // Check if this subscription is still current (hasn't been superseded by another indicator change)
                    if (subscriptionId !== currentSubscriptionId) {
                        // Dispose the table model we just created since it's no longer needed
                        tableModel[Symbol.dispose]();
                        return;
                    }

                    tableSubscriptionDisposables = new DisposableStack();

                    // Initialize the table model
                    tableModel.init({
                        queryOptions: {
                            queryKey: ['alert-indicator', this.#id, instrumentStr, indicatorRef.id],
                            queryFn: async () => ({
                                ...(await createTableApi(tablePayload)),
                                fetchMode: 'live' as const,
                            }),
                            staleTime: 0,
                            gcTime: 0,
                        },
                        initBounds: { firstRow: 0, windowSize: 1 },
                        initThrottle: 1,
                        devtoolsName: 'AlertRow',
                    });

                    tableSubscriptionDisposables.use(tableModel);

                    // Track whether we've received any data for debugging
                    let hasReceivedData = false;
                    let updateCount = 0;

                    // Set up effect to read table updates
                    tableSubscriptionDisposables.defer(
                        effect(() => {
                            const updateData = tableModel.updateSignal.value?.update;
                            updateCount++;

                            if (!updateData) {
                                if (TRACE) {
                                    this.logger.trace('Indicator value update: no data yet', {
                                        alertId: this.#id,
                                        indicatorId: indicatorRef.id,
                                        instrument: instrumentStr,
                                        updateCount,
                                        connectionStatus: tableModel.connectionStatus.value,
                                    });
                                }
                                return;
                            }

                            hasReceivedData = true;
                            const currentValue = updateData[INDICATOR_VALUE_COLUMN_ID] as number | undefined;

                            // Debug log when value is undefined (will display as "--")
                            if (currentValue === undefined) {
                                this.logger.debug('Indicator value is undefined (will display "--")', {
                                    alertId: this.#id,
                                    indicatorId: indicatorRef.id,
                                    indicatorName: indicatorEntry?.name,
                                    instrument: instrumentStr,
                                    updateCount,
                                    connectionStatus: tableModel.connectionStatus.value,
                                    rawUpdateKeys: Object.keys(updateData),
                                    columnId: INDICATOR_VALUE_COLUMN_ID,
                                });
                            } else if (TRACE) {
                                this.logger.trace('Indicator value received', {
                                    alertId: this.#id,
                                    indicatorId: indicatorRef.id,
                                    instrument: instrumentStr,
                                    value: currentValue,
                                    updateCount,
                                });
                            }

                            // For change indicators, color is based on sign (positive=green, negative=red)
                            // For other indicators (like Last Price), color is based on tick-to-tick direction
                            this.#priceDirection.value = isChangeIndicator
                                ? getDirectionFromSign(currentValue)
                                : getPriceDirectionFromPrices(previousValue, currentValue);
                            this.#indicatorValue.value = currentValue;
                            previousValue = currentValue;
                        }),
                    );

                    // Log connection status changes to help debug data flow issues
                    tableSubscriptionDisposables.defer(
                        effect(() => {
                            const status = tableModel.connectionStatus.value;
                            if (TRACE) {
                                this.logger.trace('Table connection status changed', {
                                    alertId: this.#id,
                                    indicatorId: indicatorRef.id,
                                    instrument: instrumentStr,
                                    status,
                                    hasReceivedData,
                                });
                            }
                            // Log at debug level when disconnected and we haven't received data
                            if (status === 'disconnected' && !hasReceivedData) {
                                this.logger.debug('Table disconnected before receiving indicator data', {
                                    alertId: this.#id,
                                    indicatorId: indicatorRef.id,
                                    indicatorName: indicatorEntry?.name,
                                    instrument: instrumentStr,
                                });
                            }
                        }),
                    );

                    if (TRACE) {
                        this.logger.trace('Subscribed to indicator value', {
                            alertId: this.#id,
                            indicatorId: indicatorRef.id,
                            indicatorName: indicatorEntry?.name,
                            instrument: instrumentStr,
                        });
                    }
                });
            }),
        );

        // Clean up table subscription when the view model is disposed
        this.#disposableStack.defer(() => {
            tableSubscriptionDisposables?.dispose();
        });
    }

    /**
     * Set the connection status
     */
    setConnectionStatus(status: TableStatus): void {
        this.#connectionStatus.value = status;
    }

    /**
     * Convert current UI state to a definition for saving (includes id but not server-assigned createdAt).
     * @throws Error if condition is null (view model not properly initialized)
     */
    toDefinition(): NewConditionAlertDefinition & { id: string } {
        const state = this.#state.value;
        if (state.condition === null) {
            throw new Error('Cannot convert to definition: condition is null');
        }
        return {
            type: 'condition' as const,
            id: this.#id,
            instrument: this.#instrument,
            condition: state.condition,
            enabled: state.enabled,
            notifications: buildNotifications({
                showToast: state.showToast,
                playSound: state.playSound,
                soundType: state.soundType,
                speakTts: state.speakTts,
                ttsText: state.ttsText,
                ttsVoiceUri: state.ttsVoiceUri,
                ttsRate: state.ttsRate,
                ttsPitch: state.ttsPitch,
                ttsVolume: state.ttsVolume,
            }),
            userNote: state.note ?? '',
        };
    }

    readonly [Symbol.dispose] = () => this.#disposableStack.dispose();
}
